import { Option } from '@sapphire/result';
import {
  Quota,
  SurveyFormState,
  SurveyMetadataForm,
  SurveyMetadataFormState,
  SurveyQualification,
  SurveyQualificationsFormState,
  SurveyQuotasFormState,
} from '@ps/survey-utils';

interface FormState {
  metadata: SurveyMetadataFormState;
  qualifications: SurveyQualificationsFormState;
  quotas: SurveyQuotasFormState;
}

interface SurveyFormData {
  metadata: SurveyMetadataForm;
  qualifications: SurveyQualification[];
  quotas: Quota[];
}

export class SurveyForm {
  private constructor(
    private readonly _metadata: SurveyMetadataFormState,
    private readonly _qualifications: SurveyQualificationsFormState,
    private readonly _quotas: SurveyQuotasFormState,
  ) {}

  public static from(state: FormState): SurveyForm {
    return new SurveyForm(state.metadata, state.qualifications, state.quotas);
  }

  public get data(): SurveyFormData | null {
    if (this.valid) {
      return {
        metadata: this._metadata.value as SurveyMetadataForm,
        qualifications: this._qualifications.value,
        quotas: this._quotas.value,
      };
    }

    return null;
  }

  public get error(): string | null {
    if (this.valid) {
      return null;
    }

    return this._errors()[0];
  }

  public get valid(): boolean {
    return (
      this._isSurveyFormValid(this._metadata) &&
      this._isSurveyFormValid(this._qualifications) &&
      this._isSurveyFormValid(this._quotas)
    );
  }

  public isStandardSurvey(): boolean {
    return Option.from(this._metadata.value)
      .mapInto(({ selectedLanguages, selectedCountries }) =>
        Option.some(
          selectedLanguages?.length === 1 && selectedCountries?.length === 1,
        ),
      )
      .unwrapOr(false);
  }

  public showErrors(whenMetadataComplete = false): boolean {
    const errors = this._errors();

    if (whenMetadataComplete) {
      return this._isMetadataComplete() && errors.length > 0;
    }

    return errors.length > 0;
  }

  private _errors(): string[] {
    return [
      ...this._metadata.errors.map((error) => error.message),
      ...this._qualifications.errors.map((error) => error.message),
      ...this._quotas.errors.map((error) => error.message),
    ].filter((message) => !!message) as string[];
  }

  private _isSurveyFormValid<T>(form: SurveyFormState<T>): boolean {
    return form.valid && !!form.value;
  }

  private _isMetadataComplete(): boolean {
    return !this._metadata.errors.filter(({ kind }) => kind === 'required')
      .length;
  }
}
